import React from 'react';
import { JSONSchema7 } from 'json-schema';
import { getPrimaryType } from 'src/modules/shared/utils/json-schema.util';
import { JSONSchemaFormProps } from 'src/modules/shared/types/json-schema.type';
import { StringField } from 'src/modules/shared/components/atoms/json-schema-inputs/string-field';
import { NumberField } from 'src/modules/shared/components/atoms/json-schema-inputs/number-field';
import { BooleanField } from 'src/modules/shared/components/atoms/json-schema-inputs/boolean-field';
import { Text } from 'src/modules/shared/components/atoms/text';
import { AnyOfField } from 'src/modules/shared/components/molecules/json-schema-form/any-of-field';

/**
 * Props for ArrayItemField component
 */
interface ArrayItemSchemaFieldsProps {
  parentKey: string;
  index: number;
  itemSchema: JSONSchema7;
  itemValue: unknown;
  arrayValue: unknown[];
  handleChange: (key: string, value: unknown) => void;
  disabled?: boolean;
  allFieldsOptional?: boolean;
  anyOfSelections: Record<string, number>;
  setAnyOfSelections: (selections: Record<string, number>) => void;
  JSONSchemaForm: React.ComponentType<JSONSchemaFormProps>;
}

/**
 * Component to render individual array item fields
 */
export const ArrayItemSchemaFields: React.FC<ArrayItemSchemaFieldsProps> = ({
  parentKey,
  index,
  itemSchema,
  itemValue,
  arrayValue,
  handleChange,
  disabled = false,
  allFieldsOptional = false,
  anyOfSelections,
  setAnyOfSelections,
  JSONSchemaForm,
}) => {
  const fieldType = getPrimaryType(itemSchema);
  const itemChangeHandler = (_key: string, value: unknown) => {
    const newArray = [...arrayValue];
    newArray[index] = value;
    handleChange(parentKey, newArray);
  };

  const hasUnionSchemas =
    Array.isArray(itemSchema.anyOf) && itemSchema.anyOf.length > 0;
  const hasOneOfSchemas =
    !hasUnionSchemas &&
    Array.isArray(itemSchema.oneOf) &&
    itemSchema.oneOf.length > 0;
  if (hasUnionSchemas || hasOneOfSchemas) {
    const schemaForAnyOf: JSONSchema7 = hasUnionSchemas
      ? itemSchema
      : { ...itemSchema, anyOf: itemSchema.oneOf };

    return (
      <AnyOfField
        fieldKey={`${parentKey}-${index}`}
        schema={schemaForAnyOf}
        value={itemValue}
        handleChange={itemChangeHandler}
        anyOfSelections={anyOfSelections}
        setAnyOfSelections={setAnyOfSelections}
        disabled={disabled}
        allFieldsOptional={false}
        JSONSchemaForm={JSONSchemaForm}
      />
    );
  }

  switch (fieldType) {
    case 'string': {
      return (
        <StringField
          id={`field-${parentKey}-${index}`}
          schema={itemSchema}
          value={itemValue}
          onChange={(newValue) =>
            itemChangeHandler(`${parentKey}-${index}`, newValue)
          }
          disabled={disabled}
        />
      );
    }
    case 'number':
    case 'integer': {
      return (
        <NumberField
          id={`field-${parentKey}-${index}`}
          schema={itemSchema}
          value={itemValue}
          onChange={(newValue) =>
            itemChangeHandler(`${parentKey}-${index}`, newValue)
          }
          disabled={disabled}
          fieldType={fieldType}
        />
      );
    }
    case 'boolean': {
      return (
        <BooleanField
          id={`field-${parentKey}-${index}`}
          schema={itemSchema}
          value={itemValue}
          onChange={(newValue) =>
            itemChangeHandler(`${parentKey}-${index}`, newValue)
          }
          disabled={disabled}
        />
      );
    }
    case 'object': {
      if (!itemSchema.properties) break;

      return (
        <JSONSchemaForm
          schema={itemSchema}
          formData={(itemValue as Record<string, unknown>) || {}}
          onChange={(data: Record<string, unknown>) => {
            const newArray = [...arrayValue];
            newArray[index] = data;
            handleChange(parentKey, newArray);
          }}
          disabled={disabled}
          allFieldsOptional={allFieldsOptional}
        />
      );
    }
    default: {
      return (
        <Text size="small" color="neutral">
          {/* TODO: check when implementing i18n */}
          Tipo no soportado: {fieldType}
        </Text>
      );
    }
  }
};
